import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import BitacoraJornada from "./BitacoraJornada";

interface Props {
  deviceId: string;
  unitName: string;
  plate?: string | null;
  isDarkMode: boolean;
  onClose: () => void;
  onAssignDriver?: (() => void) | null;
}

// Panel lateral con la bitácora de la unidad, como el panel de unidad de Mapon:
// el mapa sigue visible y usable al lado (sin velo). Esc o la X lo cierran y el
// foco vuelve a donde estaba.
export default function BitacoraDrawer({ deviceId, unitName, plate, isDarkMode, onClose, onAssignDriver }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-label={`Eventos de conducción · ${unitName}`}
      // z-[60]: encima del riel (z-30), del panel de la lista (z-40) y del menú del riel (z-[55]).
      className={`fixed top-0 right-0 bottom-0 z-[60] w-full sm:w-[440px] flex flex-col shadow-2xl border-l outline-none motion-safe:animate-slideIn ${
        isDarkMode ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200"
      }`}
    >
      <header className={`flex items-center gap-3 px-4 py-3 border-b ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
        <span className="w-9 h-9 rounded-lg bg-primary-500/15 text-primary-500 flex items-center justify-center" aria-hidden>
          <i className="ri-steering-2-line text-lg" />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Eventos de conducción</p>
          <h2 className={`text-base font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {unitName}
            {plate && plate !== "—" && <span className={`ml-2 text-xs font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{plate}</span>}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            isDarkMode ? "text-gray-300 hover:bg-white/10" : "text-gray-600 hover:bg-black/5"
          }`}
        >
          <i className="ri-close-line text-xl" aria-hidden />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <BitacoraJornada deviceId={deviceId} unitName={unitName} isDarkMode={isDarkMode} onAssignDriver={onAssignDriver} />
      </div>
    </div>,
    document.body,
  );
}
